import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { PawPrint, ChevronLeft, Send, CheckCircle2, MessageSquare } from "lucide-react";
import MobileNavMenu from "@/components/MobileNavMenu";
import { toast } from "sonner";
import { useT } from "@/lib/translations";

export default function ContactPage() {
  const L = useT();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(L("Lütfen ad, e-posta ve mesaj alanlarını doldurun."));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || L("Mesaj gönderilemedi. Lütfen tekrar deneyin."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <nav className="glass-nav sticky top-0 z-50 border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <PawPrint className="h-8 w-8 text-primary" strokeWidth={1.5} />
              <span className="font-heading text-2xl font-semibold text-foreground">KAHU</span>
            </Link>
            <MobileNavMenu />
          </div>
        </div>
      </nav>

      <section className="py-12 md:py-20">
        <div className="max-w-2xl mx-auto px-4 md:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors" data-testid="contact-back">
            <ChevronLeft className="w-4 h-4" />
            {L("Ana Sayfa")}
          </Link>

          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-4">
              <MessageSquare className="w-7 h-7 text-primary" strokeWidth={1.8} />
            </div>
            <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-3">
              {L("Bize Ulaş")}
            </h1>
            <p className="text-muted-foreground text-base md:text-lg">
              {L("Bir sorunuz, geri bildiriminiz veya işbirliği teklifiniz mi var? Yazın, en kısa sürede dönelim.")}
            </p>
          </div>

          {sent ? (
            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="p-8 text-center space-y-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/15">
                  <CheckCircle2 className="w-8 h-8 text-primary" strokeWidth={1.8} />
                </div>
                <h2 className="font-heading text-2xl">{L("Mesajınız iletildi 🐾")}</h2>
                <p className="text-muted-foreground">
                  {L("Yazdıklarınız ekibimize ulaştı.")} <strong>{form.email}</strong> {L("adresinden en kısa sürede dönüş yapacağız.")}
                </p>
                <Link to="/" data-testid="contact-back-home">
                  <Button className="btn-pill bg-primary text-primary-foreground mt-2 font-semibold transition-all duration-200 hover:bg-primary hover:text-white hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/30">
                    {L("Ana Sayfaya Dön")}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-border/60">
              <CardContent className="p-6 md:p-8">
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="mb-1.5 block text-sm">{L("Ad Soyad *")}</Label>
                      <Input
                        id="name"
                        value={form.name}
                        onChange={handleChange("name")}
                        placeholder={L("Adınız ve soyadınız")}
                        required
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="mb-1.5 block text-sm">{L("E-posta *")}</Label>
                      <Input
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange("email")}
                        placeholder="ornek@email.com"
                        required
                        data-testid="contact-email"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="subject" className="mb-1.5 block text-sm">{L("Konu")}</Label>
                    <Input
                      id="subject"
                      value={form.subject}
                      onChange={handleChange("subject")}
                      placeholder={L("Mesajınızın konusu (opsiyonel)")}
                      data-testid="contact-subject"
                    />
                  </div>
                  <div>
                    <Label htmlFor="message" className="mb-1.5 block text-sm">{L("Mesajınız *")}</Label>
                    <Textarea
                      id="message"
                      value={form.message}
                      onChange={handleChange("message")}
                      placeholder={L("Yazmak istediklerinizi paylaşın...")}
                      rows={6}
                      required
                      maxLength={4000}
                      data-testid="contact-message"
                    />
                    <p className="text-xs text-muted-foreground mt-1.5 text-right">
                      {form.message.length}/4000
                    </p>
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full btn-pill bg-primary text-primary-foreground h-12 font-semibold transition-all duration-200 hover:bg-primary hover:text-white hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/30 disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="contact-submit"
                  >
                    {submitting ? (
                      L("Gönderiliyor...")
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        {L("Mesajı Gönder")}
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>
    </div>
  );
}
